import { useState, type FormEvent } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { loadStudents } from '../../../Redux/studentsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature } from '../../../Models/enums';
import { StudentService } from '../../../Services/StudentService';
import { Modal } from '../../SharedArea/Modal/Modal';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { PremiumGate } from '../Premium/PremiumGate';
import { GoogleContactPicker } from './GoogleContactPicker';
import './Contacts.css';
import { contactDisplayName, contactDisplayRelation, studentDisplayName } from '../../../Utils/DisplayNameUtil';

/**
 * Who to contact about each student.
 *
 * A payment message usually goes to a parent rather than to the student, so a
 * student can carry several contacts and each one decides on its own whether
 * it receives them. The reminder screen picks the one marked for payments,
 * which is why that flag is the only thing on this screen that is not a
 * plain field.
 */
export function Contacts() {
  return (
    <PremiumGate feature={PremiumFeature.PaymentReminders}>
      <ContactsInner />
    </PremiumGate>
  );
}

function ContactsInner() {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const students = useAppSelector(state => state.students.items);
  const [adding, setAdding] = useState<string | null>(null);
  const [importing, setImporting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function remove(id: number) {
    try {
      await StudentService.removeContact(id);
      void dispatch(loadStudents());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  async function togglePayments(id: number, receivesPayments: boolean) {
    try {
      await StudentService.updateContact(id, { receivesPayments: receivesPayments ? 1 : 0 });
      void dispatch(loadStudents());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  if (error) return <ErrorState code={error} onRetry={() => setError(null)} />;
  if (students.length === 0) {
    return <EmptyState title={tr('noStudents')} hint={tr('noStudentsHint')} />;
  }

  return (
    <section className="contacts">
      <h1 className="screen-title">{tr('navContacts')}</h1>

      <ul className="contact-groups">
        {students.filter(student => !student.archived).map(student => (
          <li key={student.name} className="contact-group">
            <header className="contact-group-header">
              <span className="contact-group-name">{studentDisplayName(student, language)}</span>
              <span className="contact-group-actions">
                <button type="button" className="ghost-button" onClick={() => setImporting(student.name)}>
                  {tr('importFromGoogle')}
                </button>
                <button type="button" className="ghost-button" onClick={() => setAdding(student.name)}>
                  {tr('add')}
                </button>
              </span>
            </header>
            {student.contacts.length === 0
              ? <p className="dashboard-quiet">{tr('noContacts')}</p>
              : (
                <ul className="contact-rows">
                  {student.contacts.map(contact => (
                    <li key={contact.id}>
                      <span className="contact-name">{contactDisplayName(contact, language)}</span>
                      {contact.relation && <span className="dashboard-quiet">{contactDisplayRelation(contact, language)}</span>}
                      {contact.phone && <bdi className="contact-phone">{contact.phone}</bdi>}
                      <label className="check-label">
                        <input
                          type="checkbox"
                          checked={Boolean(contact.receivesPayments)}
                          onChange={event => void togglePayments(contact.id, event.target.checked)}
                        />
                        <span>{tr('receivesPayments')}</span>
                      </label>
                      <button type="button" className="ghost-button is-destructive" onClick={() => void remove(contact.id)}>
                        {tr('delete')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
          </li>
        ))}
      </ul>

      {importing && (
        <GoogleContactPicker
          student={importing}
          onClose={() => setImporting(null)}
          onImported={() => {
            setImporting(null);
            void dispatch(loadStudents());
          }}
        />
      )}

      {adding && (
        <AddContact
          student={adding}
          onClose={() => setAdding(null)}
          onDone={() => {
            setAdding(null);
            void dispatch(loadStudents());
          }}
        />
      )}
    </section>
  );
}

function AddContact({
  student, onClose, onDone
}: { student: string; onClose: () => void; onDone: () => void }) {
  const { tr } = useTranslation();
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await StudentService.addContact(student, {
        name, relation, phone, receivesPayments: true
      });
      onDone();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tr('addContact')}
      subtitle={student}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="submit" form="add-contact" className="primary-button" disabled={busy || !name.trim()}>
            {tr('save')}
          </button>
        </>
      }
    >
      <form id="add-contact" className="student-editor" onSubmit={submit}>
        <label className="field is-wide">
          <span>{tr('contactName')}</span>
          <input className="text-input" value={name} onChange={e => setName(e.target.value)} required />
        </label>
        <label className="field">
          <span>{tr('relation')}</span>
          <input className="text-input" value={relation} onChange={e => setRelation(e.target.value)} />
        </label>
        <label className="field">
          <span>{tr('contactPhone')}</span>
          <input className="text-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
        </label>
      </form>
    </Modal>
  );
}
